'use client';
import { Button, Input, AppleLoginButton, Modal } from '@get-mosh/msh-shared';
import React, { useEffect, useState, useCallback, FormEvent } from 'react';
import GoogleLoginButton from 'src/components/GoogleLoginButton';
import LayoutBasic from 'src/views/Layout/Basic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/pro-solid-svg-icons';
import { validateEmail } from 'src/helpers/utils';
import { useCreateAccount } from 'src/hooks/User/useCreate';
import Routes from 'src/constants/Routes';
import {
    useLoginWithApple,
    AppleAuthenticationRequest,
} from 'src/hooks/User/useLogin';
import { trackClick } from 'src/analytics/Events';
import EventNames from 'src/analytics/EventNames';
import { usePathname } from 'next/navigation';
import Brands from 'src/constants/Brands';
import classNames from 'classnames';
import { isMosh, isMoshy } from 'src/env.mjs';
import Link from 'next/link';

const CreateAccount = () => {
    const { mutate: createAccount, isPending, isSuccess } = useCreateAccount();
    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState(false);
    const [emailTouched, setEmailTouched] = useState(false);
    const [validEmail, setValidEmail] = useState(false);
    const [password, setPassword] = useState('');
    const [passwordTouched, setPasswordTouched] = useState(false);
    const [passwordVisible, setPasswordVisible] = useState(false);
    const [continueDisabled, setContinueDisabled] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const pathname = usePathname();

    const togglePasswordVisibility = () => {
        setPasswordVisible(!passwordVisible);
    };

    const { mutateAsync } = useLoginWithApple();
    const handleAppleLoginSuccess = (data: AppleAuthenticationRequest) => {
        mutateAsync(data);
    };

    useEffect(() => {
        const isValid = validateEmail(email);
        setEmailError(emailTouched && email !== '' && !isValid);
        setValidEmail(isValid);
    }, [email, emailTouched]);

    useEffect(() => {
        setContinueDisabled(!(validEmail && password.length > 5));
    }, [validEmail, password]);

    const handleSubmit = useCallback(
        async (event: FormEvent) => {
            event.preventDefault();

            if (validEmail && password) {
                createAccount({ email, password });
            } else {
                if (!validEmail) setEmailTouched(true);
                if (!password) setPasswordTouched(true);
            }
        },
        [validEmail, email, password, createAccount],
    );

    return (
        <LayoutBasic className="flex flex-col items-center justify-center lg:min-h-screen">
            <form
                className="flex flex-col rounded-2xl border border-grey-200 bg-white p-6 shadow"
                onSubmit={handleSubmit}
            >
                <h3 className="text-left">Create an account</h3>
                <div className="flex flex-col items-center gap-2 py-6">
                    <GoogleLoginButton />
                    <AppleLoginButton
                        onLoginSuccess={handleAppleLoginSuccess}
                    />
                </div>
                <div className="flex flex-row items-center gap-4 pb-6">
                    <div className="flex-1 border-t border-grey-200"></div>
                    <span className="text-sm text-grey-400">
                        or continue with email
                    </span>
                    <div className="flex-1 border-t border-grey-200"></div>
                </div>
                <div className={`relative ${!validEmail ? 'pb-4' : 'pb-2'}`}>
                    <Input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        placeholder="john@example.com"
                        error={
                            emailError
                                ? 'Please enter a valid email address'
                                : ''
                        }
                        onFocus={() => emailTouched && setEmailTouched(false)}
                        onBlur={() => !emailTouched && setEmailTouched(true)}
                        showFeedback={!validEmail}
                    />
                </div>
                {validEmail && (
                    <div className="relative pb-4">
                        <Input
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            placeholder="Password"
                            type={passwordVisible ? 'text' : 'password'}
                            onFocus={() =>
                                passwordTouched && setPasswordTouched(false)
                            }
                            onBlur={() => {
                                if (!emailTouched) {
                                    setEmailTouched(true);
                                    setEmail(email.trim());
                                }
                            }}
                            showFeedback={false}
                        />
                        <FontAwesomeIcon
                            icon={passwordVisible ? faEyeSlash : faEye}
                            className="absolute right-3 top-5 -translate-y-1 transform cursor-pointer text-grey-300"
                            onClick={togglePasswordVisibility}
                        />
                    </div>
                )}

                <Button
                    type="submit"
                    disabled={continueDisabled}
                    isLoading={isPending || isSuccess}
                >
                    Continue
                </Button>
                <div className="pt-4 text-center">
                    <span className="text-xs text-grey-400">
                        Already have an account?{' '}
                    </span>
                    <Link
                        className={classNames(
                            'font-demi text-xs hover:underline',
                            {
                                'text-green-faint-dark': isMosh,
                                'text-blue-info': isMoshy,
                            },
                        )}
                        href={Routes.LOGIN}
                    >
                        Log in
                    </Link>
                </div>
            </form>
            <span className="max-w-[343px] py-4 text-xs text-grey-400 lg:max-w-[430px]">
                By clicking “Continue” you agree to our{' '}
                <Link className="underline" href={Routes.TERMS_OF_USE}>
                    Terms & Conditions
                </Link>{' '}
                and{' '}
                <Link className="underline" href={Routes.PRIVACY_POLICY}>
                    Privacy Policy
                </Link>
                , and consent to telehealth. By completing the quiz you agree to
                our{' '}
                <button
                    className="cursor-pointer underline"
                    onClick={() => {
                        setIsModalOpen(true);
                        trackClick(EventNames.MODAL_OPENED, {
                            path: pathname,
                            brand: Brands.MOSH,
                            label: 'Default notifications settings',
                            title: 'Sign up to Mosh',
                            url: window.location.href,
                        });
                    }}
                >
                    Default notifications settings
                </button>
            </span>
            <Modal
                className="!border-0 !p-0"
                isOpen={isModalOpen}
                onModalClose={(value) => {
                    setIsModalOpen(value || false);
                }}
            >
                <div className="px-4 py-8 sm:w-[375px] md:h-auto md:w-[458px]">
                    <h3 className="text-left">Default notifications</h3>
                    <span className="text-left text-base font-normal text-grey-400">
                        We subscribe you to the following email notifications
                        and announcements when you create an account. Adjust
                        your preferences from your account settings or
                        unsubscribe at any time.
                    </span>
                    <div className="flex flex-row items-center gap-4 pt-6">
                        <span className="pb-2 font-demi text-xs text-grey-400">
                            Adjustable
                        </span>
                        <div className="flex-1 border-t border-grey-200"></div>
                    </div>
                    <div className="g-3 inline-flex items-start justify-start">
                        <span className="text-left font-demi">
                            <ul className="list-disc pl-5">
                                <li>Your health journey</li>
                                <li>Marketing offers and updates</li>
                                <li>Product updates</li>
                                <li>Newsletter</li>
                                <li>Surveys & feedback</li>
                            </ul>
                        </span>
                    </div>
                </div>
            </Modal>
        </LayoutBasic>
    );
};
export default CreateAccount;
